import { getStaffRegistry } from "@/lib/staff/staffRegistrySingleton";
import { apiClient } from "@/lib/apiClient";
import { apiPath } from "@/lib/apiPath";
import { dateSource } from "@/lib/dateSource";
import { resolveMediaUrl } from "@/lib/resolveMediaUrl";
import { DateTime } from 'luxon';
import type { Specialist } from "@/lib/types";
import type { Staff } from "@/lib/types/staff";
import {getWeekStart} from "@/lib/utils"



/**
 * In-flight request deduplication for schedule API.
 * Keyed by the request ID or params string.
 */
const inFlightRequests: Record<string, Promise<any>> = {};

/**
 * Track which requests have been loaded.
 * Uses a string key to differentiate request ID 5 by startDate.
 */
const loadedRequests = new Set<string>();

/**
 * Map request IDs to their full query strings (from src/mocks/requests.json).
 * These are hardcoded here for efficiency and to avoid file IO at runtime.
 */
function getRequest2Params3month(baseDate: DateTime = dateSource.now()): string {
  const start = baseDate.minus({ months: 1 }).startOf("month");
  const end = baseDate.plus({ months: 2 }).endOf("month");
  const dates: string[] = [];
  let d = start;
  while (d <= end) {
    dates.push(d.toISODate());
    d = d.plus({ days: 1 });
  }
  const dateParams = dates.map(date => `dates%5B%5D=${encodeURIComponent(date)}`).join("&");
  const includeParams = "include%5B%5D=tt_day_schedule_masters";
  return `${dateParams}&${includeParams}`;
}

function getRequest0Params(baseDate: DateTime = dateSource.now()): string {
  const dateStr = baseDate.toISODate();
  return (
    `dates%5B%5D=${encodeURIComponent(dateStr)}` +
    "&include%5B%5D=tt_day_schedule_resource_instances.resource_instance.resource" +
    "&include%5B%5D=tt_day_schedule_resource_instances.resource_instance" +
    "&include%5B%5D=tt_day_schedule_resource_instances" +
    "&include%5B%5D=tt_day_schedule_masters.master" +
    "&include%5B%5D=tt_day_schedule_masters" +
    "&include%5B%5D=tt_day_schedule_masters.tt_day_master_grid_status" +
    "&new_timetable_schedule_from_legacy=1"
  );
}

function getRequest5Params(startDate: string = dateSource.now().toISODate()): string {
  const start = DateTime.fromISO(startDate, { zone: 'UTC' });
  const dates: string[] = [];
  for (let i = 0; i < 7; i++) {
    const d = start.plus({ days: i });
    dates.push(d.toISODate());
  }
  const dateParams = dates.map(date => `dates[]=${encodeURIComponent(date)}`).join("&");
  return (
    `${dateParams}` +
    "&include[]=tt_day_schedule_resource_instances.resource_instance.resource" +
    "&include[]=tt_day_schedule_resource_instances.resource_instance" +
    "&include[]=tt_day_schedule_resource_instances" +
    "&include[]=tt_day_schedule_masters.master" +
    "&include[]=tt_day_schedule_masters" +
    "&include[]=tt_day_schedule_masters.tt_day_master_grid_status" +
    "&schedule_for_week=1"
  );
}

const requestParams: Record<number, string | (() => string) | ((startDate?: string) => string)> = {
  0: getRequest0Params,
  2: getRequest2Params3month,
  5: getRequest5Params
};

/**
 * Helper to load and merge a request by ID, only if not already loaded.
 * Deduplicates in-flight requests.
 */
async function loadRequest(id: number, startDate?: string, baseDate?: DateTime): Promise<void> {
  let cacheKey: string;
  if (id === 5 && startDate) {
    cacheKey = `${id}-${startDate}`;
  } else if (id === 2 && baseDate) {
    cacheKey = `${id}-${baseDate.toFormat("yyyy-MM")}`;
  } else {
    cacheKey = id.toString();
  }
  if (loadedRequests.has(cacheKey)) return;
  let params = requestParams[id];
  if (!params) throw new Error(`No params for request ID ${id}`);
  if (typeof params === "function") {
    if (id === 5 && startDate) {
      params = params(startDate);
    } else if (id === 2 && baseDate) {
      params = (params as (d: DateTime) => string)(baseDate);
    } else {
      params = params();
    }
  }
  // @ts-ignore
  if (inFlightRequests[params]) {
    await inFlightRequests[params];
    return;
  }
  inFlightRequests[params] = apiClient.get(`/api/v1/company/timetable/schedule?${params}`)
    .then(({ data }) => {
      const staffRegistry = getStaffRegistry();
      staffRegistry.parseResponse(data);
      loadedRequests.add(cacheKey);
      delete inFlightRequests[params];
    });
  await inFlightRequests[params];
}

/**
 * Fetches staff and schedule data for a given date, populates the registry, and returns the normalized daily specialists.
 * @param date ISO date string (YYYY-MM-DD)
 */
export async function fetchDailySpecialists(date: string): Promise<Specialist[]> {
  await loadRequest(0, undefined);
  await loadRequest(2, undefined);
  await loadRequest(5, getWeekStart(date));
  // bring in weight from the availability endpoint so sorting has the data
  await fetchStaffAvailability({ dates: [date] });
  return getStaffRegistry().getDailySchedule(date);
}

/**
 * Fetches all staff data and returns a map of staffId → staff object.
 */
export async function fetchStaffByIdMap(): Promise<Record<number, any>> {
  await loadRequest(0, undefined);
  await loadRequest(2, undefined);
  return getStaffRegistry().getStaffByIdMap();
}

/**
 * Fetches the company's full active-staff roster (every non-fired master).
 *
 * Unlike every other staff source here this is NOT derived from the schedule,
 * so it includes masters whose first shift is months away — the waitlist and
 * the header staff selector both need to offer those. The endpoint's filter
 * mirrors the waitlist create validator, so the list can never contain a master
 * the API would then reject.
 *
 * Results are merged into the StaffRegistry so registry consumers pick up the
 * names/weights too (the registry keeps a previously-known weight when the
 * incoming row has none).
 */
export async function fetchActiveStaff(): Promise<Staff[]> {
  const { data } = await apiClient.get(apiPath("/company/staff/active"));
  const registry = getStaffRegistry();

  const staff: Staff[] = (data?.staff ?? []).map((row: any) => {
    const entry: Staff = {
      id: Number(row.id),
      name: row.name ?? "",
      specialization: row.specialization ?? "",
      avatar: resolveMediaUrl(row.avatar_url),
      ...(typeof row.weight === "number" ? { weight: row.weight } : {}),
      ...(row.information ? { information: row.information } : {}),
    };
    registry.addOrUpdateStaff(entry);
    return entry;
  });

  return staff;
}


/**
 * Fetches the full staff schedule mapping: { [date: string]: number[] }
 */
export async function fetchStaffScheduleByDate(forDay: DateTime = dateSource.now()): Promise<Record<string, number[]>> {
  await loadRequest(0, undefined);
  await loadRequest(2, undefined, forDay);
  const weekStart = getWeekStart(forDay.toISODate());
  await loadRequest(5, weekStart);
  const scheduleMap: Record<string, number[]> = {};
  // @ts-ignore: access private property for compatibility
  const scheduleByDate: Map<string, any> = getStaffRegistry().scheduleByDate || new Map();
  for (const [date, staffArr] of scheduleByDate.entries()) {
    scheduleMap[date] = staffArr.map((s: any) => typeof s.id === "string" ? Number(s.id) : s.id);
  }
  return scheduleMap;
}

/**
 * Fetches week schedule data and returns an array of { date, specialistIds } for the week starting at startDate.
 * @param startDate ISO date string (YYYY-MM-DD)
 */
export async function fetchWeekSchedule(startDate: string): Promise<{ date: string; specialistIds: number[] }[]> {
  await loadRequest(0, undefined);
  await loadRequest(2, undefined);
  await loadRequest(5, startDate);
  return getStaffRegistry().getWeekSchedule(startDate);
}

/**
 * Fetches week schedule data for a specific staff member and returns an array of { date, hasSchedule }.
 * @param staffId The staff member's ID
 * @param weekDates Array of ISO date strings (e.g., ["2025-05-19", ...])
 */
export async function fetchWeekScheduleForStaff(staffId: number, weekDates: string[]): Promise<{ date: string; hasSchedule: boolean }[]> {
  await loadRequest(0, undefined);
  await loadRequest(2, undefined);
  await loadRequest(5, weekDates.length > 0 ? weekDates[0] : dateSource.now().toISODate());
  return getStaffRegistry().getWeekScheduleForStaff(staffId, weekDates);
}

/**
 * Fetch staff availability for optional dates[], masters[], and services[].
 * NOTE: Do not hardcode '/public'; apiPath injects scope.
 */
export async function fetchStaffAvailability(
  opts?: { dates?: string[]; masters?: number[]; services?: number[] }
): Promise<unknown> {
  const url = apiPath("/company/timetable/staff_availability");

  const params = new URLSearchParams();
  opts?.dates?.forEach((d) => d && params.append("dates[]", d));
  opts?.masters?.forEach((id) => Number.isFinite(id) && params.append("masters[]", String(id)));
  opts?.services?.forEach((id) => Number.isFinite(id) && params.append("services[]", String(id)));

  const { data } = await apiClient.get(url, { params });
  getStaffRegistry().mergeStaffAvailability(data);
  /**
   * Backward-compatible contract:
   * - Main app can keep relying on registry side-effects above.
   * - Availability hooks may consume this fresh payload directly to avoid stale
   *   cross-query mixing in registry-derived availability slices.
   */
  return data;
}
